import { ActionKeys } from '~/actions/constants';
import { type ActionTypes } from '~/actions/index';
import { NotificationType } from '~/types/EmailNotifications';

export interface NotificationsState {
  readonly autoDeliveredEmailCustomText: Maybe<string>;
  readonly autoOrderEmailCustomText: Maybe<string>;
  readonly autoOrderEmailDelayMinutes: Maybe<number>;
  readonly isAutoDeliveredEmailEnabled: boolean;
  readonly isAutoOrderEmailEnabled: boolean;
  readonly isLoadingNotifications: boolean;
  readonly orderArrivedEmailSettingSubmitFailed: boolean;
  readonly orderArrivedEmailSettingSubmitSuccess: boolean;
  readonly orderShippedEmailSettingSubmitFailed: boolean;
  readonly orderShippedEmailSettingSubmitSuccess: boolean;
  readonly sampleDeliveredEmailFailed: boolean;
  readonly sampleDeliveredEmailSent: boolean;
  readonly sampleShippedEmailFailed: boolean;
  readonly sampleShippedEmailSent: boolean;
  readonly sendingSampleDeliveredEmail: boolean;
  readonly sendingSampleShippedEmail: boolean;
}

export const initialState: NotificationsState = {
  autoDeliveredEmailCustomText: null,
  autoOrderEmailCustomText: null,
  autoOrderEmailDelayMinutes: null,
  isAutoDeliveredEmailEnabled: false,
  isAutoOrderEmailEnabled: false,
  isLoadingNotifications: false,
  orderArrivedEmailSettingSubmitFailed: false,
  orderArrivedEmailSettingSubmitSuccess: false,
  orderShippedEmailSettingSubmitFailed: false,
  orderShippedEmailSettingSubmitSuccess: false,
  sampleDeliveredEmailFailed: false,
  sampleDeliveredEmailSent: false,
  sampleShippedEmailFailed: false,
  sampleShippedEmailSent: false,
  sendingSampleDeliveredEmail: false,
  sendingSampleShippedEmail: false,
};

const notificationsReducer = (
  state: NotificationsState = initialState,
  action: ActionTypes,
): NotificationsState => {
  switch (action.type) {
    case ActionKeys.FETCH_NOTIFICATIONS:
      return {
        ...state,
        isLoadingNotifications: true,
      };
    case ActionKeys.FETCH_NOTIFICATIONS_SUCCESS:
      return {
        ...state,
        autoDeliveredEmailCustomText: action.payload.autoDeliveredEmailCustomText,
        autoOrderEmailCustomText: action.payload.autoOrderEmailCustomText,
        autoOrderEmailDelayMinutes: action.payload.autoOrderEmailDelayMinutes,
        isAutoDeliveredEmailEnabled: action.payload.autoDeliveredEmailNotifications,
        isAutoOrderEmailEnabled: action.payload.autoEmailNotifications,
        isLoadingNotifications: false,
      };
    case ActionKeys.UPDATE_ORDER_SHIPPED_EMAIL_SETTINGS:
      return {
        ...state,
        orderShippedEmailSettingSubmitFailed: false,
        orderShippedEmailSettingSubmitSuccess: false,
      };
    case ActionKeys.UPDATE_ORDER_SHIPPED_EMAIL_SETTINGS_ERROR:
      return {
        ...state,
        orderShippedEmailSettingSubmitFailed: true,
      };
    case ActionKeys.UPDATE_ORDER_SHIPPED_EMAIL_SETTINGS_SUCCESS:
      return {
        ...state,
        autoOrderEmailCustomText: action.payload.orderShippedFormFields.orderShippingEmailContent,
        autoOrderEmailDelayMinutes:
          action.payload.orderShippedFormFields.orderShippingEmailDelaySendDuration,
        isAutoOrderEmailEnabled: action.payload.orderShippedFormFields.orderShippingEmailEnabled,
        orderShippedEmailSettingSubmitSuccess: true,
      };
    case ActionKeys.UPDATE_ORDER_ARRIVED_EMAIL_SETTINGS:
      return {
        ...state,
        orderArrivedEmailSettingSubmitFailed: false,
        orderArrivedEmailSettingSubmitSuccess: false,
      };
    case ActionKeys.UPDATE_ORDER_ARRIVED_EMAIL_SETTINGS_ERROR:
      return {
        ...state,
        orderArrivedEmailSettingSubmitFailed: true,
      };
    case ActionKeys.UPDATE_ORDER_ARRIVED_EMAIL_SETTINGS_SUCCESS:
      return {
        ...state,
        autoDeliveredEmailCustomText:
          action.payload.orderArrivedFormFields.orderArrivedEmailContent,
        isAutoDeliveredEmailEnabled: action.payload.orderArrivedFormFields.orderArrivedEmailEnabled,
        orderArrivedEmailSettingSubmitSuccess: true,
      };
    case ActionKeys.SEND_SAMPLE_EMAIL:
      if (action.notificationType === NotificationType.Shipped) {
        return {
          ...state,
          sampleShippedEmailFailed: false,
          sampleShippedEmailSent: false,
          sendingSampleShippedEmail: true,
        };
      }
      return {
        ...state,
        sampleDeliveredEmailFailed: false,
        sampleDeliveredEmailSent: false,
        sendingSampleDeliveredEmail: true,
      };
    case ActionKeys.SEND_SAMPLE_EMAIL_SUCCESS:
      if (action.notificationType === NotificationType.Shipped) {
        return {
          ...state,
          sampleShippedEmailFailed: false,
          sampleShippedEmailSent: true,
          sendingSampleShippedEmail: false,
        };
      }
      return {
        ...state,
        sampleDeliveredEmailFailed: false,
        sampleDeliveredEmailSent: true,
        sendingSampleDeliveredEmail: false,
      };
    case ActionKeys.SEND_SAMPLE_EMAIL_ERROR:
      if (action.notificationType === NotificationType.Shipped) {
        return {
          ...state,
          sampleShippedEmailFailed: true,
          sampleShippedEmailSent: false,
          sendingSampleShippedEmail: false,
        };
      }
      return {
        ...state,
        sampleDeliveredEmailFailed: true,
        sampleDeliveredEmailSent: false,
        sendingSampleDeliveredEmail: false,
      };
    case ActionKeys.CLEAR_SAMPLE_EMAIL_ERRORS:
      return {
        ...state,
        sampleDeliveredEmailFailed: false,
        sampleDeliveredEmailSent: false,
        sampleShippedEmailFailed: false,
        sampleShippedEmailSent: false,
        sendingSampleDeliveredEmail: false,
        sendingSampleShippedEmail: false,
      };
    default:
      return state;
  }
};

export default notificationsReducer;
